'use client'

import { type Ref, useEffect, useImperativeHandle, useRef } from 'react'

// The site key is public by design; it pairs with TURNSTILE_SECRET_KEY on the server.
export const turnstileSiteKey = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'

declare global {
  interface Window {
    turnstile?: {
      render: (
        container: HTMLElement,
        options: {
          sitekey: string
          callback: (token: string) => void
          'expired-callback': () => void
          'error-callback': () => void
        }
      ) => string
      reset: (widgetId: string) => void
      remove: (widgetId: string) => void
    }
  }
}

// Shared across widgets so two forms on one page load the script once.
let scriptPromise: Promise<void> | null = null

const loadScript = () => {
  if (window.turnstile) return Promise.resolve()
  if (!scriptPromise) {
    scriptPromise = new Promise<void>((resolve, reject) => {
      const script = document.createElement('script')
      script.src = SCRIPT_SRC
      script.async = true
      script.onload = () => resolve()
      script.onerror = () => {
        scriptPromise = null
        reject(new Error('Turnstile script failed to load'))
      }
      document.head.appendChild(script)
    })
  }
  return scriptPromise
}

export type TurnstileHandle = { reset: () => void }

// Renders nothing when NEXT_PUBLIC_TURNSTILE_SITE_KEY is unset, so environments
// without the keys keep the forms working exactly as before.
export const Turnstile = ({
  onToken,
  ref,
}: {
  onToken: (token: string | null) => void
  ref?: Ref<TurnstileHandle>
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const widgetIdRef = useRef<string | null>(null)

  useImperativeHandle(ref, () => ({
    reset: () => {
      if (widgetIdRef.current !== null) window.turnstile?.reset(widgetIdRef.current)
    },
  }))

  useEffect(() => {
    if (!turnstileSiteKey) return

    let cancelled = false

    loadScript()
      .then(() => {
        if (cancelled || !containerRef.current || !window.turnstile) return
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: turnstileSiteKey,
          callback: (token) => onToken(token),
          'expired-callback': () => onToken(null),
          'error-callback': () => onToken(null),
        })
      })
      .catch(() => {
        // Without a token the submit button stays disabled; the widget area
        // stays empty, which is the same signal Turnstile's own error state gives.
      })

    return () => {
      cancelled = true
      if (widgetIdRef.current !== null) {
        window.turnstile?.remove(widgetIdRef.current)
        widgetIdRef.current = null
      }
    }
  }, [onToken])

  if (!turnstileSiteKey) return null

  return <div ref={containerRef} className="min-h-[65px]" />
}
